
import React from 'react';
import { LessonWidget, QuizItem, SubjectType } from '../../types';
import { CheckCircle2, XCircle, Volume2, Loader2 } from 'lucide-react';
import { Button } from '../shared';

// Import Widgets
import { MathSolver, VoicePractice } from './widgets/InteractiveWidgets';
import { VisualImageWidget, ComicStripWidget, VocabCardWidget, TextWidget, FormulaWidget, DidYouKnowWidget } from './widgets/ContentWidgets';
import { CanvasWidget } from './widgets/CanvasWidget';

interface StageProps {
    data?: any;
    styles?: any; 
    isDark?: boolean;
    subject: SubjectType;
    nodeId: string;
    onPlayAudio: (text: string) => void;
    isIntro?: boolean;
    onStartAdventure?: () => void;
    audioLoading?: boolean;
    isPlaying?: boolean;
}

export const IntroStage: React.FC<StageProps & { title: string, concept: string }> = ({ 
    title, concept, styles, isDark, isIntro, onStartAdventure, audioLoading, isPlaying 
}) => {
    const btnGradient = styles?.btnGradient || 'from-fuchsia-500 to-purple-600';
    const btnShadow = styles?.btnShadow || 'shadow-fuchsia-500/30';

    return (
        <div className="w-full flex flex-col items-center justify-center max-w-4xl mx-auto px-4 py-8">
            <h1 className={`text-5xl md:text-7xl font-black mb-12 text-center leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {title}
            </h1>
            <div className={`w-full max-w-3xl rounded-[2.5rem] p-10 md:p-14 mb-12 border shadow-sm transition-all text-center relative ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-slate-50/50 border-slate-100 shadow-slate-200/50'}`}>
                <p className={`text-xl md:text-2xl font-bold leading-relaxed italic ${isDark ? 'text-indigo-200/90' : 'text-slate-700/90'}`}>
                    “{concept}”
                </p>
            </div>
            {isIntro && (
                <div className="flex flex-col items-center gap-4">
                    <Button 
                        onClick={onStartAdventure}
                        disabled={audioLoading}
                        className={`min-w-[220px] h-16 rounded-full bg-gradient-to-r ${btnGradient} shadow-xl ${btnShadow} border-0 text-white text-lg font-black group active:scale-95 transition-all`}
                    >
                        {audioLoading ? (
                            <Loader2 className="animate-spin mr-2" size={24} />
                        ) : isPlaying ? (
                            <Volume2 className="animate-pulse mr-2" size={24} />
                        ) : (
                            <Volume2 className="mr-2" size={24} />
                        )}
                        <span>开启冒险</span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export const WidgetStage: React.FC<StageProps & { widget: LessonWidget }> = ({ widget, styles, subject, nodeId, onPlayAudio, isDark = false }) => {
    if (!widget) return null;

    // AI Normalization: If the AI puts engine/payload/problem/etc at the top level instead of in 'data'
    const widgetData = widget.data || { ...widget };
    // Remove type from data to avoid recursion or confusion
    if (widgetData.type === widget.type) delete (widgetData as any).type;

    const commonProps = { data: widgetData, subject, nodeId, isDark, onPlayAudio };

    switch (widget.type) {
        case 'visual_image': return <div className="w-full h-full max-h-[60vh] rounded-[3rem] overflow-hidden shadow-2xl"><VisualImageWidget {...commonProps} /></div>;
        case 'comic_strip': return <div className="w-full h-full"><ComicStripWidget {...commonProps} /></div>;
        case 'vocab_card': return <VocabCardWidget {...commonProps} />;
        case 'text': return <TextWidget {...commonProps} />;
        case 'magic_formula': return <FormulaWidget {...commonProps} />;
        case 'did_you_know': return <DidYouKnowWidget {...commonProps} />;
        case 'canvas': return <CanvasWidget {...commonProps} />;
        case 'step_solver': 
            return (
                <div className="w-full flex items-center justify-center">
                    <MathSolver problem={widgetData.problem} steps={widgetData.steps} isDark={isDark} />
                </div>
            );
        case 'voice_practice':
            return (
                <div className="w-full flex items-center justify-center">
                    <VoicePractice phrase={widgetData.phrase} translation={widgetData.translation} tip={widgetData.tip} isDark={isDark} onPlay={onPlayAudio} />
                </div>
            );
        default:
            return null;
    }
};

export const QuizStage: React.FC<{ quiz: QuizItem[], quizState: Record<number, number>, setQuizState: (s: any) => void, t: any, isDark?: boolean }> = ({ quiz, quizState, setQuizState, t, isDark = false }) => (
    <div className="w-full max-w-2xl mx-auto flex flex-col justify-center gap-6">
        {quiz?.map((q, idx) => (
            <div key={idx} className={`backdrop-blur-xl rounded-[2.5rem] p-8 md:p-12 border shadow-2xl relative transition-colors ${isDark ? 'bg-black/40 border-white/10' : 'bg-slate-50/80 border-slate-100'}`}>
                <span className="text-indigo-400 font-bold tracking-widest text-xs uppercase mb-4 block">{t.quiz}</span>
                <p className={`font-black text-2xl md:text-3xl mb-10 leading-snug ${isDark ? 'text-white' : 'text-slate-900'}`}>{q.q}</p>
                <div className="grid grid-cols-1 gap-4">
                    {q.options.map((opt, oid) => {
                        const selected = quizState[idx] === oid;
                        const answered = quizState[idx] !== undefined;
                        const correct = oid === q.a;
                        let cardClass = isDark ? "bg-white/5 border-white/10 text-indigo-100 hover:bg-white/10" : "bg-white border-slate-100 text-slate-700 hover:bg-slate-50";
                        let icon = <div className={`w-6 h-6 rounded-full border-2 ${isDark ? 'border-white/20' : 'border-slate-300'}`}></div>;
                        if (answered) {
                            if (correct) { cardClass = "bg-emerald-500/20 border-emerald-500 text-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.2)]"; icon = <CheckCircle2 size={24} className="text-emerald-500" />; }
                            else if (selected) { cardClass = "bg-red-500/20 border-red-500 text-red-600 opacity-50"; icon = <XCircle size={24} className="text-red-500" />; }
                            else cardClass = "opacity-30 grayscale";
                        } else if (selected) { cardClass = "bg-indigo-600 border-indigo-400 text-white shadow-lg"; icon = <div className="w-6 h-6 rounded-full border-4 border-white"></div>; }
                        return (
                            <button key={oid} disabled={answered} onClick={() => setQuizState({ ...quizState, [idx]: oid })} className={`p-6 rounded-2xl font-bold text-lg text-left transition-all flex items-center justify-between border-2 ${cardClass}`}>
                                {opt}
                                <div className="shrink-0 ml-4">{icon}</div>
                            </button>
                        );
                    })}
                </div>
            </div>
        ))}
    </div>
);
